import { useCallback } from 'react';

import { endOfDay, format, isAfter, isBefore, isValid, startOfDay } from 'date-fns';

import useControlled from '@ecp/common/src/hooks/useControlled';
import usePeriod, { handlePeriod } from '@ecp/common/src/components/periodSearch/usePeriod';

const usePeriodSearch = ({
  onChange,
  fromDate: fromDateProp,
  toDate: toDateProp,
  period: periodProp,
  defaultFromDate,
  defaultToDate,
  defaultPeriod,
  periods,
  format: formatProps,
  enableRadioSearch,
  // to be deleted
  unControlled,
  onSearchClick,
}) => {
  let {
    datesProps,
    from: initialFromDate,
    to: initialToDate,
    period: initialPeriod,
  } = usePeriod(fromDateProp, toDateProp, periodProp, defaultFromDate, defaultToDate, defaultPeriod, periods);

  const { value: periodDates, setValueWhenUncontrolled: setPeriodDates } = useControlled({
    value: datesProps,
    defaultValue: { from: initialFromDate, to: initialToDate, period: initialPeriod },
  });

  const handleSearchClick = useCallback(() => {
    const changedPeriodParam = {
      ...periodDates,
      formattedFrom: isValid(periodDates.from) ? format(startOfDay(periodDates.from), formatProps) : undefined,
      formattedTo: isValid(periodDates.to) ? format(endOfDay(periodDates.to), formatProps) : undefined,
    };
    onSearchClick?.({ ...changedPeriodParam });
  }, [formatProps, onSearchClick, periodDates]);

  const setAndCallPeriodDates = useCallback(
    (dates, isPeriodSearch = false) => {
      setPeriodDates({ ...dates });

      const { from, to } = dates;
      const changedPeriodParam = {
        ...dates,
        formattedFrom: isValid(from) ? format(startOfDay(from), formatProps) : undefined,
        formattedTo: isValid(to) ? format(endOfDay(to), formatProps) : undefined,
      };
      onChange?.({ ...changedPeriodParam }, isPeriodSearch || unControlled);
    },
    [formatProps, onChange, setPeriodDates, unControlled]
  );

  const handlePeriodClick = useCallback(
    ({ target: { value } }) => {
      if (value) {
        const selectedPeriod = periods.find((option) => option.value === value);
        setAndCallPeriodDates(handlePeriod(selectedPeriod), enableRadioSearch);
      }
    },
    [enableRadioSearch, periods, setAndCallPeriodDates]
  );

  const handleFromDateChange = useCallback(
    (from) => {
      const { to } = periodDates;
      setAndCallPeriodDates({ from: from && isAfter(from, to) ? to : from, to, period: { value: null } });
    },
    [periodDates, setAndCallPeriodDates]
  );

  const handleToDateChange = useCallback(
    (to) => {
      const { from } = periodDates;
      setAndCallPeriodDates({ from, to: to && isBefore(to, from) ? from : to, period: { value: null } });
    },
    [periodDates, setAndCallPeriodDates]
  );

  const handleAllDateChange = useCallback(
    (from, to) => {
      setAndCallPeriodDates({ from, to, period: { value: null } });
    },
    [setAndCallPeriodDates]
  );

  return {
    periodDates,
    periods,
    handlePeriodClick,
    handleFromDateChange,
    handleToDateChange,
    handleSearchClick,
    handleAllDateChange,
  };
};

export default usePeriodSearch;
